"use client";

import "@/app/v2/_components/contact-list/rollout.css";

import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import { motion } from "motion/react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { RolloutLinkProvider, CredentialForm } from "@rollout/link-react";
import { Column } from "@/app/v2/_components/column/Column";
import { Row } from "@/app/v2/_components/row/Row";
import { Text } from "@/app/v2/_components/text/Text";
import { ConnectCrmButtons } from "@/app/v2/_components/contact-list/connect-crm-buttons";
import { Button } from "@/app/v2/_components/button/button";
import { getCrmName, primaryCrmSlugs, secondaryCrmSlugs } from "@/config/crm-logos";

const ease = [0.16, 1, 0.3, 1] as const;

interface BucketerConnectProps {
  onConnected: (credentialId: string) => void;
}

export function BucketerConnect({ onConnected }: BucketerConnectProps) {
  const [appKey, setAppKey] = useState<string | null>(null);
  const [isAuthInputsVisible, setIsAuthInputsVisible] = useState(false);
  const [showAllCrms, setShowAllCrms] = useState(false);

  useEffect(() => {
    if (!appKey) {
      setIsAuthInputsVisible(false);
      return;
    }

    const checkVisibility = () => {
      if (typeof document === "undefined") {
        setIsAuthInputsVisible(false);
        return;
      }
      const element = document.querySelector(".universal-AuthUserInputs-root");
      const groupElement = document.querySelector(".universal-Group-root");
      setIsAuthInputsVisible(!!element || !!groupElement);
    };

    checkVisibility();

    const observer = new MutationObserver(() => {
      checkVisibility();
    });

    if (typeof document !== "undefined" && document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, [appKey]);

  const fetchToken = useCallback(async (): Promise<string> => {
    // Use the unauthenticated bucketer token endpoint (no Convex auth needed)
    const response = await fetch("/api/bucketer/rollout-token", {
      method: "POST",
    });
    if (!response.ok) {
      throw new Error("Failed to get token");
    }
    const data = (await response.json()) as { token: string };
    return data.token;
  }, []);

  const handleCredentialAdded = useCallback(
    async (credential: { id: string; appKey: string }) => {
      onConnected(credential.id);
    },
    [onConnected],
  );

  /**
   * Use this if you want to show this form on a page that is not authenticated.
   */
  const anonymousRolloutProvider = (
    <RolloutLinkProvider token={fetchToken}>
      {appKey ? (
        <Column wide>
          <Button size="sm" variant="outline" onClick={() => setAppKey(null)} className="w-fit mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Column className="p-6 bg-muted/50 rounded-lg border border-border" wide>
            <Row tightGap align between>
              <Row tightGap align>
                <Image src={`/images/crms/${appKey}.svg`} width={32} height={32} alt={appKey} className="w-8 h-8" />
                <Text fat>Connect your {getCrmName(appKey)} account</Text>
              </Row>
              <Row shrink>{!isAuthInputsVisible && <Loader2 className="h-6 w-6 animate-spin" />}</Row>
            </Row>
            <CredentialForm appKey={appKey} onCredentialAdded={handleCredentialAdded} showUserTermsLink={false} />
          </Column>
        </Column>
      ) : (
        <Column wide>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(144px,1fr))] gap-3 justify-between w-full">
            {(showAllCrms ? [...primaryCrmSlugs, ...secondaryCrmSlugs] : primaryCrmSlugs).map((connector) => (
              <div
                key={connector}
                className="flex h-36 w-full items-center justify-center bg-white shadow-xs rounded-lg hover:bg-muted/50 border border-border cursor-pointer transition-colors"
                onClick={() => setAppKey(connector)}
              >
                <Column center>
                  <Image src={`/images/crms/${connector}.svg`} width={32} height={32} alt={connector} />
                  <Text sm className="text-center mt-2">
                    {getCrmName(connector)}
                  </Text>
                </Column>
              </div>
            ))}
          </div>
          {!showAllCrms && (
            <div className="flex justify-center">
              <Button variant="link" size="sm" onClick={() => setShowAllCrms(true)}>
                View all CRMs
              </Button>
            </div>
          )}
        </Column>
      )}
    </RolloutLinkProvider>
  );

  return (
    <section className="rollout-embed flex flex-col w-full h-full justify-start ">
      <div className="mx-auto w-full px-4 md:px-6 ">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <ConnectCrmButtons />
        </motion.div>
      </div>
    </section>
  );
}
